import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 font-heading mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleCategory = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '91n.com 官方指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91n.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91n.com' },
      ...(articleCategory
        ? [{ '@type': 'ListItem', position: 2, name: articleCategory.name, item: `https://91n.com/category/${articleCategory.slug}` }]
        : []),
      { '@type': 'ListItem', position: articleCategory ? 3 : 2, name: article.title, item: `https://91n.com/article/${article.slug}` },
    ],
  };

  const breadcrumbs = [
    { name: '首页', path: '/' },
    ...(articleCategory ? [{ name: articleCategory.name, path: `/category/${articleCategory.slug}` }] : []),
    { name: article.title, path: `/article/${article.slug}` },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - 91n.com 官方指南`}
        description={article.excerpt}
        keywords={`91n.com,${article.tags.join(',')},91n.com使用指南`}
        canonicalUrl={`https://91n.com/article/${article.slug}`}
        ogType="article"
        breadcrumbs={breadcrumbs}
        schemaData={[articleSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-20">
        <article>
          {/* Article Header */}
          <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto">
              {articleCategory && (
                <Link
                  to={`/category/${articleCategory.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:text-accent-700 transition-colors mb-4 cursor-pointer"
                >
                  <i className={`${articleCategory.icon} text-xs`}></i>
                  {articleCategory.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4 font-heading">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-xs"></i> {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-xs"></i> {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-xs"></i> 阅读约 {article.readTime}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-5">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 bg-background-200/70 px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-3xl mx-auto">
              <div className="aspect-[16/9] rounded-xl overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="w-full px-4 md:px-6 pb-16">
            <div
              className="max-w-3xl mx-auto prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-14 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h3 className="text-xl font-bold text-foreground-950 font-heading mb-8">
                  相关文章推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-foreground-400">{ra.readTime}</span>
                        </div>
                        <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}